import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Board from '../components/Board';
import { PageHeader, NextMove, useTitle } from '../components/Layout';
import { useVisit } from '../progress';
import { fromFen, applyMove, START_FEN } from '../chess';
import { careerMoves, experiences } from '../data';

const START = fromFen(START_FEN);

// Position right after white's move in step `n` (1-based), black's reply not yet played.
function positionAt(n) {
  const plies = careerMoves.flatMap((m) => m.plies).slice(0, n * 2 - 1);
  return plies.reduce(applyMove, START);
}

function detailFor(move) {
  const x = experiences.find((e) => e.company === move.ref);
  return { title: x.title, org: x.company, period: x.period, location: x.location, highlights: x.highlights };
}

export default function Moves() {
  useTitle('Moves');
  useVisit('moves');
  const total = careerMoves.length;
  const [step, setStep] = useState(1);
  const go = useCallback((n) => setStep(Math.min(total, Math.max(1, n))), [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest('input, textarea, [contenteditable]')) return;
      if (e.key === 'ArrowRight') go(step + 1);
      if (e.key === 'ArrowLeft') go(step - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, step]);

  const move = careerMoves[step - 1];
  const detail = detailFor(move);
  const position = useMemo(() => positionAt(step), [step]);

  return (
    <div className="page">
      <PageHeader index="02" eyebrow="Moves" title="The game so far.">
        My career, played as an Italian Game. Step through each move with the arrows or your keyboard.
      </PageHeader>

      <div className="moves-layout">
        <div className="moves-board">
          <Board position={position} lastMove={move.plies[0]} ariaLabel={`Position after ${move.n}. ${move.white}`} />
          <div className="stepper">
            <button type="button" className="icon-btn" onClick={() => go(1)} disabled={step === 1} aria-label="First move">
              ⏮
            </button>
            <button type="button" className="icon-btn" onClick={() => go(step - 1)} disabled={step === 1} aria-label="Previous move">
              ←
            </button>
            <span className="stepper-count mono">
              {step} / {total}
            </span>
            <button type="button" className="icon-btn" onClick={() => go(step + 1)} disabled={step === total} aria-label="Next move">
              →
            </button>
            <button type="button" className="icon-btn" onClick={() => go(total)} disabled={step === total} aria-label="Last move">
              ⏭
            </button>
          </div>
        </div>

        <div className="moves-side">
          <ol className="pgn" aria-label="Move list">
            {careerMoves.map((m) => (
              <li key={m.n}>
                <button
                  type="button"
                  className={`pgn-row ${m.n === step ? 'is-active' : ''}`}
                  aria-current={m.n === step ? 'step' : undefined}
                  onClick={() => go(m.n)}
                >
                  <span className="pgn-num mono">{m.n}.</span>
                  <span className="pgn-white mono">{m.white}</span>
                  <span className="pgn-ref">{m.ref}</span>
                  <span className="pgn-black mono">{m.black}</span>
                </button>
              </li>
            ))}
            <li>
              <Link to="/endgame" className="pgn-row pgn-next">
                <span className="pgn-num mono">{total + 1}.</span>
                <span className="pgn-white mono">?</span>
                <span className="pgn-ref">Your move</span>
                <span className="pgn-black mono">→</span>
              </Link>
            </li>
          </ol>

          <article className="card move-card" aria-live="polite">
            <p className="eyebrow">
              <span className="mono">
                {move.n}. {move.white}
              </span>{' '}
              · Experience
            </p>
            <p className="move-idea">{move.idea}</p>
            <div className="row-between">
              <div>
                <h2 className="card-title">{detail.title}</h2>
                <p className="card-sub">{detail.org}</p>
              </div>
              <p className="meta">
                {detail.period}
                {detail.location && (
                  <>
                    <br />
                    {detail.location}
                  </>
                )}
              </p>
            </div>
            <ul className="bullets">
              {detail.highlights.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          </article>
        </div>
      </div>

      <NextMove current="/moves" />
    </div>
  );
}
